import { createContext, useContext, useEffect } from 'react';

/**
 * 화면 새로고침(2026-10-19 way — 노선 탭 새로고침 요청 → 「각 메뉴에」).
 *
 * 화면 탭 줄의 「↻」 하나로 **지금 화면만** 다시 읽는다. 브라우저 새로고침(F5)과 달리 고른 조건·검색어·쪽·열린 탭이 그대로다.
 * - 공용 목록 훅(`DataTable useList`)을 쓰는 화면은 그 목록들이 **같은 조건으로** 다시 조회한다(`usePageRefresh` 의 숫자가 deps).
 * - 그 훅을 하나도 안 쓰는 화면(출납일보·편성 변경 등 자체 조회)은 화면을 **다시 연다**(처음 상태 — 계정·화면 메모리에 기억된 조건은 그대로).
 *   어느 쪽인지는 화면이 스스로 알린다(`usePageRefresh` 가 마운트돼 있으면 「조건 유지」).
 * 화면 탭(KeptPage)마다 따로다 — 숨은 화면은 건드리지 않는다.
 */
export interface PageRefresh {
  tick: number;
  /** 이 화면에 「같은 조건으로 다시 읽기」를 받는 목록이 있다고 알린다. 돌려준 함수로 해제. */
  claim: () => () => void;
  /** 화면 안 「↻ 새로고침」 버튼(#357) — 탭 줄 ↻ 와 같은 일. 폰(탭 줄 없음)에서도 쓴다. */
  request: () => void;
}

export const PageRefreshContext = createContext<PageRefresh>({ tick: 0, claim: () => () => undefined, request: () => undefined });

/** 화면 안에 두는 새로고침 함수(#357). */
export function useRefreshRequest(): () => void {
  return useContext(PageRefreshContext).request;
}

/** 목록 조회 effect 의 deps 에 넣는다 — 새로고침을 누르면 숫자가 바뀐다. */
export function usePageRefresh(): number {
  const ctx = useContext(PageRefreshContext);
  const { claim } = ctx;
  useEffect(() => claim(), [claim]);
  return ctx.tick;
}

/** 화면 탭 줄 → 그 화면(KeptPage). 키 = 화면 탭 키. */
const subs = new Map<string, Set<() => void>>();
export function onRefreshRequest(key: string, f: () => void): () => void {
  const set = subs.get(key) ?? new Set();
  set.add(f);
  subs.set(key, set);
  return () => { set.delete(f); };
}
export function requestRefresh(key: string): void {
  subs.get(key)?.forEach((f) => f());
}
